import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smartphone,
  Watch, 
  Laptop, 
  Car, 
  Headphones, 
  Gift,
  CreditCard,
  Home,
  Gamepad2,
  Camera,
  Trophy,
  ArrowLeft,
  ArrowRight,
  Pause,
  Play,
  FileText
} from 'lucide-react';

const PrizeShowcase = ({ className = "" }) => {
  const prizes = [
    {
      icon: Home,
      name: "Property",
      value: "Worth up to ₹75,00,000",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      image: "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg",
      category: "Property"
    },
    {
      icon: Smartphone,
      name: "Latest Smartphone",
      value: "Worth up to ₹1,50,000",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      image: "https://images.unsplash.com/photo-1727093493864-0bcbd16c7e6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzbWFydHBob25lfGVufDB8fHx8MTc1NzgzNTQ3Nnww&ixlib=rb-4.1.0&q=85",
      category: "Smartphone"
    },
    {
      icon: Car,
      name: "Brand New Car",
      value: "Worth up to ₹15,00,000",
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      image: "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg",
      category: "Car"
    },
    {
      icon: Watch,
      name: "Premium Smartwatch",
      value: "Worth up to ₹1,00,000",
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      image: "https://images.unsplash.com/photo-1660844817855-3ecc7ef21f12",
      category: "Smartwatch"
    },
    {
      icon: Laptop,
      name: "High-End Laptop",
      value: "Worth up to ₹2,50,000",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      image: "https://images.unsplash.com/photo-1754928864131-21917af96dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsYXB0b3B8ZW58MHx8fHwxNzU3ODM1NTA5fDA&ixlib=rb-4.1.0&q=85",
      category: "Laptop"
    },
    {
      icon: FileText,
      name: "Digital Certificate",
      value: "Participation Certificate",
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-50",
      image: "/images/certificate.svg",
      category: "Certificate"
    }
  ];

  const [currentPrizeIndex, setCurrentPrizeIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Preload all images for better performance
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = prizes.map((prize) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = prize.image;
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.log('Some images failed to preload, but continuing...');
        setImagesLoaded(true);
      }
    };

    preloadImages();
  }, []);

  useEffect(() => {
    if (!isHovered && imagesLoaded) {
      const interval = setInterval(() => {
        setCurrentPrizeIndex((prev) => (prev + 1) % prizes.length);
      }, 3000); // Balanced timing - every 3 seconds

      return () => clearInterval(interval);
    }
  }, [isHovered, prizes.length, imagesLoaded]);

  // Optimized click handler for instant transitions
  const handleDotClick = (index) => {
    if (index === currentPrizeIndex) return;
    
    setCurrentPrizeIndex(index);
  };

  const currentPrize = prizes[currentPrizeIndex];
  const IconComponent = currentPrize.icon;

  return (
    <div 
      className={`${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPrizeIndex}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ 
            duration: 0.3,
            ease: "easeInOut"
          }}
          className="space-y-4"
        >
          {/* Next Card Info for Mobile - Above the card with smaller, better UI */}
          <div className="block md:hidden mb-2">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 mx-auto max-w-fit shadow-md border border-gray-300"
            >
              <span className="text-xs text-gray-600">
                Next: <span className="font-semibold text-gray-800">{prizes[(currentPrizeIndex + 1) % prizes.length].name}</span>
              </span>
            </motion.div>
          </div>
          {/* Main Prize Image Container */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
          {/* Animated background pattern */}
          <motion.div
            className="absolute inset-0 opacity-10"
            animate={{
              backgroundPosition: ["0% 0%", "100% 100%"],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            style={{
              backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
              backgroundSize: "20px 20px",
            }}
          />

          {/* Optimized Prize Image Container - Simplified */}
          <motion.div
            className="relative group"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <div className="relative w-full h-64 sm:h-72 md:h-80 lg:h-96 overflow-hidden rounded-3xl bg-gray-200">
              {/* Loading placeholder */}
              {!imagesLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse rounded-3xl flex items-center justify-center">
                  <div className="text-gray-500 text-lg">Loading...</div>
                </div>
              )}
              
              {/* Optimized image */}
              <img 
                src={currentPrize.image} 
                alt={currentPrize.name}
                className={`w-full h-full object-cover transition-transform duration-200 ease-out group-hover:scale-105 ${
                  imagesLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading="eager"
                decoding="async"
                fetchpriority="high"
                onLoad={() => setImagesLoaded(true)}
              />
              
              {/* Simple gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
              
              {/* Category Badge - Simple */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-semibold text-gray-800 shadow-lg">
                {currentPrize.category}
              </div>
              
              {/* Prize Value - Simple */}
              <div className="absolute bottom-4 right-4 bg-red-600 rounded-lg px-4 py-2 shadow-lg">
                <span className="text-white font-bold text-sm sm:text-base">{currentPrize.value}</span>
              </div>

              {/* Prize Icon - Simple */}
              <div className={`absolute bottom-4 left-4 bg-gradient-to-br ${currentPrize.color} w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-lg`}>
                <IconComponent className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>

              {/* Prize Name - Simple */}
              <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 text-center">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg">
                  {currentPrize.name}
                </h3>
                <span className="text-lg sm:text-xl font-semibold text-yellow-300 drop-shadow-lg">
                  {currentPrize.value}
                </span>
              </div>

              {/* Next Card Info - Simple */}
              <div className="hidden md:block absolute top-16 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                <div className="text-xs text-gray-600 text-center">
                  <span className="block text-gray-500">🎁 Up Next</span>
                  <span className="font-semibold text-gray-800">{prizes[(currentPrizeIndex + 1) % prizes.length].name}</span>
                </div>
              </div>
            </div>
          </motion.div>

            {/* Play/Pause Control - Top Right */}
            <div className="absolute top-4 right-4 z-20">
              <motion.button
                onClick={() => setIsHovered(!isHovered)}
                className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title={isHovered ? "Resume" : "Pause"}
              >
                {isHovered ? <Play className="h-4 w-4 text-gray-700" /> : <Pause className="h-4 w-4 text-gray-700" />}
              </motion.button>
            </div>
          </div>

          {/* Simple Dot Navigation */}
          <div className="flex items-center justify-center space-x-3 pt-6">
            {prizes.map((_, index) => (
              <button
                key={index}
                className={`rounded-full transition-all duration-200 cursor-pointer ${
                  index === currentPrizeIndex 
                    ? 'w-10 h-3 bg-red-600' 
                    : 'w-3 h-3 bg-gray-400 hover:bg-gray-600'
                }`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PrizeShowcase;